import Echo from 'laravel-echo'
import Pusher from 'pusher-js'
import { getAccessToken } from '@/core/auth/storage'

/** Realtime UI sync only — user-facing alerts use Firebase FCM push. */
declare global {
  interface Window {
    Pusher: typeof Pusher
    Echo?: Echo<'reverb'>
  }
}

let echo: Echo<'reverb'> | null = null
let echoAuthToken: string | null = null

export function isEchoConfigured(): boolean {
  return String(import.meta.env.VITE_REVERB_APP_KEY ?? '').trim().length > 0
}

/** Hostname only — .env must not include http:// (Pusher uses wsHost as host, not URL). */
function normalizeReverbHost(raw: string | undefined): string {
  const fallback =
    typeof window !== 'undefined' ? window.location.hostname : '127.0.0.1'
  const input = (raw ?? '').trim()
  if (!input) return fallback

  try {
    if (/^https?:\/\//i.test(input)) {
      const url = new URL(input)
      return url.hostname === 'localhost' ? '127.0.0.1' : url.hostname
    }
  } catch {
    // fall through
  }

  const stripped = input.replace(/^https?:\/\/?/i, '').split('/')[0]?.split(':')[0] ?? ''
  if (!stripped) return fallback
  return stripped === 'localhost' ? '127.0.0.1' : stripped
}

function resolveReverbPort(scheme: string, configuredRaw: string | undefined): number {
  const useTls = scheme === 'https'
  if (configuredRaw != null && String(configuredRaw).trim() !== '') {
    return Number(configuredRaw)
  }
  if (import.meta.env.DEV && typeof window !== 'undefined') {
    const fromPage = Number(window.location.port)
    return fromPage > 0 ? fromPage : useTls ? 443 : 5173
  }
  return useTls ? 443 : 8080
}

/**
 * HTTPS multi-tenant pages must use same-origin WSS (/app → nginx → reverb).
 * Stale builds with VITE_REVERB_HOST=127.0.0.1 / Docker DNS "reverb" would fail in the browser.
 */
function resolveReverbEndpoint(): { host: string; port: number; useTls: boolean } {
  let scheme = (import.meta.env.VITE_REVERB_SCHEME as string) || 'http'
  let host = normalizeReverbHost(import.meta.env.VITE_REVERB_HOST as string | undefined)
  let port = resolveReverbPort(scheme, import.meta.env.VITE_REVERB_PORT as string | undefined)

  const pageIsHttps =
    typeof window !== 'undefined' && window.location.protocol === 'https:'

  if (pageIsHttps) {
    const loopback = host === '127.0.0.1' || host === 'localhost'
    // Docker Compose service name — only reachable inside the cluster, never from browsers.
    const dockerInternal = host === 'reverb' || host === 'api' || host === 'nginx'
    const dockerSpaPort = port === 8081 || port === 8082 || port === 8083
    if (scheme !== 'https' || loopback || dockerInternal || dockerSpaPort) {
      scheme = 'https'
      host = window.location.hostname
      port = 443
    }
  }

  return { host, port, useTls: scheme === 'https' }
}

/** Dev: relative → Vite proxy. Prod split-host: full URL tới API :8080. */
function resolveBroadcastAuthEndpoint(): string {
  if (import.meta.env.DEV) {
    return '/api/broadcasting/auth'
  }

  // HTTPS multi-tenant: same-origin auth (Host → tenant.resolve), never api.* cross-origin.
  const pageIsHttps =
    typeof window !== 'undefined' && window.location.protocol === 'https:'
  if (pageIsHttps) {
    return '/api/broadcasting/auth'
  }

  const apiUrl = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '')
  if (apiUrl) {
    return `${apiUrl}/api/broadcasting/auth`
  }

  const apiBase = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api/v1'
  if (apiBase.startsWith('http://') || apiBase.startsWith('https://')) {
    return `${apiBase.replace(/\/api\/v1\/?$/, '')}/api/broadcasting/auth`
  }

  return '/api/broadcasting/auth'
}

function authorizePrivateChannel(
  authEndpoint: string,
  channelName: string,
  socketId: string,
): Promise<Record<string, unknown>> {
  const token = getAccessToken() || ''
  return fetch(authEndpoint, {
    method: 'POST',
    credentials: 'omit',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      socket_id: socketId,
      channel_name: channelName,
    }),
  }).then(async (res) => {
    if (!res.ok) {
      throw new Error(`Broadcast auth failed (${res.status})`)
    }
    return (await res.json()) as Record<string, unknown>
  })
}

/** Returns null when Reverb key is missing — FCM push still works without WS. */
export function useEcho(): Echo<'reverb'> | null {
  if (!isEchoConfigured()) return null

  const token = getAccessToken() || ''
  if (echo && echoAuthToken === token) return echo
  if (echo) {
    echo.disconnect()
    echo = null
  }

  window.Pusher = Pusher

  const { host, port, useTls } = resolveReverbEndpoint()
  const key = String(import.meta.env.VITE_REVERB_APP_KEY).trim()
  const authEndpoint = resolveBroadcastAuthEndpoint()
  echoAuthToken = token

  echo = new Echo({
    broadcaster: 'reverb',
    key,
    wsHost: host,
    wsPort: port,
    wssPort: port,
    forceTLS: useTls,
    enabledTransports: useTls ? ['wss'] : ['ws'],
    disableStats: true,
    // Keepalive — reduce idle drops behind Host Nginx / ALB.
    activityTimeout: 30_000,
    pongTimeout: 15_000,
    authEndpoint,
    // Read Bearer on each private-channel auth so token refresh does not leave a dead PAT.
    authorizer: (channel) => ({
      authorize: (socketId, callback) => {
        void authorizePrivateChannel(authEndpoint, channel.name, socketId)
          .then((data) => callback(null, data))
          .catch((error: Error) => callback(error, null))
      },
    }),
  })

  window.Echo = echo
  return echo
}

export function resetEcho(): void {
  echo?.disconnect()
  echo = null
  echoAuthToken = null
  delete window.Echo
}
